import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import isWebview from 'is-ua-webview';
import SVGCamera from '../../../common/assets/svg/icons/SVGCamera';
import SVGPhoneVariant from '../../../common/assets/svg/icons/SVGPhone-variant';
import SVGHeartKeyhole from '../../../common/assets/svg/icons/orange/SVGHeartKeyhole';
import Button, { ButtonStyle } from '../../../common/components/Button/Button';
import ButtonContainer from '../../../common/components/Button/ButtonContainer';
import TitleContainer from '../../../common/components/TitleContainer/TitleContainer';
import useUserAgent from '../../../common/hooks/useUserAgent';
import InfoBox from './InfoBox';
import { ValidationContext } from '../../../providers/ValidationContext';
import FlowMode from '../../../providers/validation/FlowMode';
import { FlowConfigContext } from '../../../providers/FlowConfigContext';
import { isAndroid, isIOS } from 'react-device-detect';
import { Action } from '../../../config';

export interface CameraChooserProps {
  showWebcamButton?: boolean;
  showPhoneButton?: boolean;
  title: string;
  loading?: boolean;
  enableBreakoutFlow?: boolean;
  description?: {
    text: string;
    link?: string;
  };
  handleChoosePhone: () => void;
  handleChooseWebcam: () => void;
  handleChooseBreakoutFlow: () => void;
}

export default function CameraChooser({
  title,
  description,
  showWebcamButton = true,
  showPhoneButton = true,
  handleChoosePhone,
  handleChooseWebcam,
  handleChooseBreakoutFlow,
  loading,
  enableBreakoutFlow,
}: CameraChooserProps): JSX.Element {
  const { t } = useTranslation();
  const userAgent = useUserAgent();
  const isAndroidWebview = isWebview(userAgent) && userAgent.includes('Android');
  const isIphone = userAgent.includes('iPhone');
  const { flowMode } = useContext(ValidationContext);
  const [isLandscape, setIsLandscape] = useState(window.matchMedia('(orientation: landscape)').matches);
  const { action } = useContext(FlowConfigContext);

  // The webview message should only show if the user started the flow on mobile (FlowMode.full),
  // not when it was handed off to mobile (FlowMode.partial)
  let showWebViewMessage = (isAndroidWebview || isIphone) && flowMode !== FlowMode.partial;

  if (enableBreakoutFlow) {
    // If we're doing the breakout flow, disable all the other buttons and messages
    showWebViewMessage = false;
    showPhoneButton = false;
    showWebcamButton = false;
  }

  useEffect(() => {
    const mql = window.matchMedia('(orientation: landscape)');
    const onChange = (e: MediaQueryListEvent) => {
      setIsLandscape(e.matches);
    };

    mql.addEventListener('change', onChange);

    return () => {
      mql.removeEventListener('change', onChange);
    };
  }, []);

  return (
    <div className="bg-background">
      <TitleContainer center marginBottomClass="mb-0">
        <h1 className="mb-4" data-testid="FACE_ISSUANCE_HEADER">
          {title}
        </h1>
        <div className="flex justify-center h-52" data-testid="CHOOSE_WEB_OR_MOBILE_SELECTOR">
          <video
            className="rounded-48px"
            autoPlay
            loop
            muted
            poster="video/face-scan-poster.webp"
            playsInline
            disableRemotePlayback
          >
            <source src="video/face-scan.mp4" type="video/mp4" />
          </video>
        </div>
        <>
          {description && (
            <div className="flex items-center gap-x-4 pt-2">
              <div className="flex-shrink-0">
                <SVGHeartKeyhole />
              </div>
              <div className="text-sm text-textParagraph text-left" data-testid="FACE_ISSUANCE_DESCRIPTION">
                {description.text}
                {description.link && (
                  <span
                    role="button"
                    onClick={() => window.open(description.link)}
                    onKeyDown={() => window.open(description.link)}
                    tabIndex={-1}
                    className="ml-2 text-sm textLinkDetail"
                  >
                    {t('screens.faceUniqueness.learnMoreLink')}
                  </span>
                )}
              </div>
            </div>
          )}
        </>
        {/* In a chrome extension, we hide this because user only has 1 choice */}
        {showWebcamButton && showPhoneButton ? (
          <div
            className="text-lg leading-6 my-4 pass-status-text"
            data-testid="screens.chooseWebOrMobile.chooseCameraLabel"
          >
            {t('screens.chooseWebOrMobile.chooseCameraLabel')}
          </div>
        ) : (
          <div className="my-4 " data-testid="screens.chooseWebOrMobile.chooseCameraLabel" />
        )}
      </TitleContainer>

      {/* Facetec requires portrait mode on mobile devices */}
      {isLandscape && !enableBreakoutFlow && (isAndroid || isIOS) && action === Action.LINK && (
        <div className="mb-4">
          <InfoBox descriptionText={t('screens.chooseWebOrMobile.portraitModeMessage')} />
        </div>
      )}

      {enableBreakoutFlow && (
        <ButtonContainer>
          <Button
            style={ButtonStyle.DEFAULT}
            dataTestid="CHOOSE_BREAKOUT_FLOW"
            disabled={false}
            onClick={handleChooseBreakoutFlow}
          >
            <div className="flex items-center font-normal gap-x-2">
              <span>{t('screens.chooseWebOrMobile.breakoutLabel')}</span>
              <SVGPhoneVariant className="fill-current flex-shrink-0" />
            </div>
          </Button>
        </ButtonContainer>
      )}

      {!showPhoneButton && !showWebcamButton && !enableBreakoutFlow ? (
        <InfoBox descriptionText={t('screens.chooseWebOrMobile.webcamPermissionMessage')} />
      ) : (
        <>
          <div className="flex justify-center gap-x-4">
            {showPhoneButton && !loading && (
              <ButtonContainer>
                <Button
                  style={ButtonStyle.DEFAULT}
                  dataTestid="CHOOSE_CONTINUE_ON_MOBILE"
                  disabled={false}
                  onClick={handleChoosePhone}
                >
                  <div className="flex items-center text-textButton font-normal gap-x-2">
                    <span>{t('screens.chooseWebOrMobile.phoneLabel')}</span>
                    <SVGPhoneVariant className="fill-current flex-shrink-0" />
                  </div>
                </Button>
              </ButtonContainer>
            )}
            {showWebcamButton && (
              <ButtonContainer>
                <Button
                  style={ButtonStyle.DEFAULT}
                  dataTestid="CHOOSE_CONTINUE_ON_WEB"
                  disabled={false}
                  onClick={handleChooseWebcam}
                  loading={loading}
                >
                  <div className="flex items-center text-textButton font-normal">
                    <span>
                      {showPhoneButton
                        ? t('screens.chooseWebOrMobile.webcamLabel')
                        : t('screens.chooseWebOrMobile.continueLabel')}
                    </span>
                    <SVGCamera className="fill-current flex-shrink-0" />
                  </div>
                </Button>
              </ButtonContainer>
            )}
          </div>
          {showWebViewMessage ? (
            <InfoBox descriptionText={t('screens.chooseWebOrMobile.webviewMessage')} />
          ) : (
            <div className="full-width-ruler" />
          )}
        </>
      )}
    </div>
  );
}
